import { useParams, useNavigate, Link as RouterLink } from "react-router-dom";
import { formatHoursMinutes } from "@/lib/video-metadata";
import { useState, useMemo, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { CreditCard, QrCode, FileText, ArrowLeft, Copy, Clock, Wallet, Sparkles } from "lucide-react";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "@/hooks/use-toast";
import { usePlanQuery, useAddUserTagMutation, useValidateCouponLazyQuery, useMyCpmWalletQuery, useRedeemWalletForPlanMutation } from "@/generated/graphql";
import { GET_MY_DATA, MY_CPM_WALLET } from "@/queries/queries";
import { authAxios } from "@/lib/axios";
import { useApolloClient, useQuery } from "@apollo/client";
import { CheckCircle } from "lucide-react";
import { cpf, cnpj } from "cpf-cnpj-validator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { translateCouponError } from "@/lib/translate-coupon-error";
import { useCurrency, getPlanPrices } from "@/hooks/use-currency";
import { computeDiscountedPrice } from "@/lib/billing/computeDiscountedPrice";
import { computeDiscountAmount } from "@/lib/billing/computeDiscountAmount";
import { formatPixCountdown } from "@/lib/billing/formatPixCountdown";
import { trackEvent } from "@/lib/analytics/trackEvent";
import { resolveWalletChargePlan } from "@/lib/billing/resolveWalletChargePlan";
import { walletPlanDiscountPercent } from "@/lib/billing/walletPlanDiscountPercent";
import { resolveRedeemOutcome } from "@/lib/billing/resolveRedeemOutcome";
import { CancelPendingHoldButton } from "@/components/wallet/CancelPendingHoldButton";
import { formatCents } from "@/lib/format/currency";
import { cn } from "@/lib/utils";
import { isLiveStorePlan, isStoreManagedError, storeDisplayName, storeProviderOf } from "@/lib/billing/storeBilling";
import { isPhoneInvalidError } from "@/lib/errors/isPhoneInvalidError";

const PlanCheckout = () => {
  const { t } = useTranslation();
  const { currency, isBRL, formatCurrency } = useCurrency();
  const { data: userData } = useQuery(GET_MY_DATA);
  const storeSubscription = userData?.myData?.subscription;
  const storeProvider = isLiveStorePlan(storeSubscription) ? storeProviderOf(storeSubscription) : null;
  const storeManagedMessage = storeProvider
    ? t('billing.storeManagedCheckout', { store: storeDisplayName(storeProvider) })
    : null;
  const storeManagedErrorMessage = t('billing.storeManagedCheckout', {
    store: storeDisplayName(storeProviderOf(storeSubscription) ?? 'app_store'),
  });
  const { planId } = useParams<{ planId: string }>();
  const navigate = useNavigate();
  const apolloClient = useApolloClient();
  const [paymentMethod, setPaymentMethod] = useState<"credit_card" | "pix" | "boleto" | "wallet">("credit_card");
  const [walletRemainderMethod, setWalletRemainderMethod] = useState<"credit_card" | "pix">("credit_card");
  const [walletConfirmOpen, setWalletConfirmOpen] = useState(false);
  const [walletSubmitting, setWalletSubmitting] = useState(false);
  const [redeemWalletForPlan] = useRedeemWalletForPlanMutation();
  const { data: walletData, refetch: refetchWallet } = useMyCpmWalletQuery();
  const wallet = walletData?.myCpmWallet;
  const isRedemptionHoldPending = wallet?.pending_kind === 'plan_redemption'
    || wallet?.pending_kind === 'minutes_redemption';
  const [documentType, setDocumentType] = useState<"cpf" | "cnpj">("cpf");
  const [pixFormData, setPixFormData] = useState({
    document: ""
  });
  const [isYearly, setIsYearly] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pixPaymentData, setPixPaymentData] = useState<{
    id: string;
    qr_code: string;
    qr_code_url: string;
    expires_at: string;
    status: string;
  } | null>(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const timeUpdateIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const [addUserTag] = useAddUserTagMutation();
  const [couponCode, setCouponCode] = useState("");
  const [validatedCoupon, setValidatedCoupon] = useState<{
    id: string;
    code: string;
    discount: number;
    cycles?: number | null;
    expiry_date?: string | null;
  } | null>(null);
  const [validateCoupon, { loading: couponValidating }] = useValidateCouponLazyQuery();
  const couponFromUrlRef = useRef<string | null>(null);

  const { data: planData, loading: planLoading } = usePlanQuery({
    variables: { id: planId! },
    skip: !planId
  });
  const plan = planData?.plan;

  const isTester = useMemo(() => {
    return userData?.myData?.tags?.some((tag: any) => tag.id === 'tester');
  }, [userData?.myData?.tags]);

  useEffect(() => {
    // Pegar o parâmetro yearly da URL se existir
    const searchParams = new URLSearchParams(window.location.search);
    const yearlyParam = searchParams.get('yearly');
    if (yearlyParam === 'true') {
      setIsYearly(true);
    }

    // Pegar o parâmetro coupon da URL se existir
    const couponParam = searchParams.get('coupon');
    if (couponParam) {
      const couponUpper = couponParam.toUpperCase();
      setCouponCode(couponUpper);
      couponFromUrlRef.current = couponUpper;
    }
  }, []);

  // Auto-validate coupon from URL when plan is loaded
  useEffect(() => {
    const autoValidateCoupon = async () => {
      // Only auto-validate if coupon came from URL and plan is loaded
      if (!plan?.documentId || !couponFromUrlRef.current || couponCode !== couponFromUrlRef.current) {
        return;
      }

      try {
        const { data } = await validateCoupon({
          variables: {
            code: couponCode.trim(),
            planId: plan.documentId,
            recurrency: isYearly ? 'year' : 'month'
          }
        });

        if (data?.validateCoupon?.valid && data.validateCoupon.coupon) {
          setValidatedCoupon(data.validateCoupon.coupon);
          toast({
            title: t('checkout.couponApplied', { code: data.validateCoupon.coupon.code }),
            description: t('checkout.discount', { percent: data.validateCoupon.coupon.discount }),
          });
        } else {
          setValidatedCoupon(null);
          const errorMessage = data?.validateCoupon?.error
            ? translateCouponError(data.validateCoupon.error)
            : t('checkout.couponNotValidForPlan');
          toast({
            title: t('checkout.couponInvalid'),
            description: errorMessage,
            variant: "destructive",
          });
        }
      } catch (error: any) {
        console.error('Coupon validation error:', error);
        setValidatedCoupon(null);
        toast({
          title: t('checkout.couponValidationError'),
          description: t('checkout.couponValidationErrorDesc'),
          variant: "destructive",
        });
      }
    };

    autoValidateCoupon();
  }, [plan?.documentId, couponCode, isYearly, validateCoupon]);

  // Track user entering the checkout page
  useEffect(() => {
    const trackCheckoutVisit = async () => {
      try {
        await addUserTag({ variables: { name: 'entered-checkout' } });
      } catch (error) {
        console.error('Error adding user tag:', error);
      }
    };

    trackCheckoutVisit();
  }, [addUserTag]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
      if (timeUpdateIntervalRef.current) {
        clearInterval(timeUpdateIntervalRef.current);
      }
    };
  }, []);

  // Update time display for expiration in real-time
  useEffect(() => {
    if (pixPaymentData?.expires_at) {
      // Update time immediately
      setCurrentTime(new Date());

      // Then update every second for real-time countdown
      timeUpdateIntervalRef.current = setInterval(() => {
        setCurrentTime(new Date());
      }, 1000);
    } else {
      // Clear interval when no PIX payment
      if (timeUpdateIntervalRef.current) {
        clearInterval(timeUpdateIntervalRef.current);
        timeUpdateIntervalRef.current = null;
      }
    }

    return () => {
      if (timeUpdateIntervalRef.current) {
        clearInterval(timeUpdateIntervalRef.current);
        timeUpdateIntervalRef.current = null;
      }
    };
  }, [pixPaymentData]);

  if (planLoading) {
    return (
      <Layout onStartTour={() => {}}>
        <div className="flex-1 flex items-center justify-center">
          <p className="text-muted-foreground">{t('checkout.loadingPlan')}</p>
        </div>
      </Layout>
    );
  }

  if (!plan) {
    return (
      <Layout onStartTour={() => {}}>
        <div className="flex-1 flex flex-col items-center justify-center">
          <p className="text-lg text-muted-foreground mb-4">{t('checkout.planNotFound')}</p>
          <Button onClick={() => navigate('/plans')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t('checkout.backToPlans')}
          </Button>
        </div>
      </Layout>
    );
  }

  if (storeManagedMessage) {
    return (
      <Layout onStartTour={() => {}}>
        <div className="flex-1 flex flex-col items-center justify-center">
          <p className="text-lg text-muted-foreground mb-4 text-center">{storeManagedMessage}</p>
          <Button onClick={() => navigate('/plans')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t('checkout.backToPlans')}
          </Button>
        </div>
      </Layout>
    );
  }

  const formatExpirationTime = (expiresAt: string) => {
    const countdown = formatPixCountdown(expiresAt, currentTime);
    if (countdown.expired) {
      return t('checkout.expired');
    }
    const parts = [];
    if (countdown.days > 0) parts.push(`${countdown.days}d`);
    if (countdown.hours > 0) parts.push(`${countdown.hours}h`);
    if (countdown.minutes > 0) parts.push(`${countdown.minutes}m`);
    if (countdown.seconds > 0 || parts.length === 0) parts.push(`${countdown.seconds}s`);
    return t('checkout.expiresIn', { time: parts.join(' ') });
  };

  const buildFeatures = (plan: any): Array<{ label: string; comingSoon?: boolean }> => {
    const items: Array<{ label: string; comingSoon?: boolean }> = [];
    const totalSecs = Number(plan?.processing_seconds ?? 0);
    if (totalSecs > 0) items.push({ label: t('pricing.processingTime', { time: formatHoursMinutes(totalSecs) }) });
    const maxClips = Number(plan?.max_clips ?? 0);
    if (maxClips > 0) items.push({ label: maxClips >= 1000000 ? t('pricing.unlimitedClips') : t('pricing.clipsPerMonth', { clips: maxClips }) });
    // Same audience split as /plans: clippers see `other_channels` items, everyone
    // else `own_brand`; untagged items belong to both.
    const audience = userData?.myData?.onboarding_objective === 'other_channels' ? 'other_channels' : 'own_brand';
    const desc = Array.isArray(plan?.description_items) ? plan.description_items : [];
    for (const d of desc) {
      if (d && typeof d === "object" && "label" in d && (!(d as any).for || (d as any).for === audience)) {
        const label = isBRL ? String((d as any).label) : String((d as any).label_en || (d as any).label);
        items.push({ label, comingSoon: Boolean((d as any).coming_soon) });
      } else if (typeof d === "string") {
        items.push({ label: d });
      }
    }
    return items;
  };

  const copyPixCode = async (code: string) => {
    try {
      await navigator.clipboard.writeText(code);
      toast({
        title: t('checkout.pixCodeCopied'),
        description: t('checkout.pixCodeCopiedDesc'),
      });
    } catch {
      toast({
        title: t('checkout.pixCopyError'),
        description: t('checkout.pixCopyErrorDesc'),
        variant: "destructive",
      });
    }
  };

  const checkPixPaymentStatus = async (paymentId: string) => {
    try {
      // Check if payment has expired
      if (pixPaymentData?.expires_at) {
        const expirationDate = new Date(pixPaymentData.expires_at);
        const now = new Date();
        if (now >= expirationDate) {
          // Payment expired
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
          if (timeUpdateIntervalRef.current) {
            clearInterval(timeUpdateIntervalRef.current);
            timeUpdateIntervalRef.current = null;
          }

          toast({
            title: t('checkout.qrCodeExpired'),
            description: t('checkout.qrCodeExpiredDesc'),
            variant: "destructive",
          });

          setPixPaymentData(null);
          return;
        }
      }

      const response = await authAxios.get(`/api/subscriptions/pix/${paymentId}`);

      if (response.data?.status === 'paid' || response.data?.status === 'completed') {
        // Payment successful
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
        if (timeUpdateIntervalRef.current) {
          clearInterval(timeUpdateIntervalRef.current);
          timeUpdateIntervalRef.current = null;
        }

        toast({
          title: t('checkout.paymentConfirmed'),
          description: t('checkout.paymentConfirmedDesc'),
        });

        // Refetch user data to update subscription status
        try {
          await apolloClient.refetchQueries({
            include: [GET_MY_DATA]
          });
        } catch (error) {
          console.error('Error refetching user data:', error);
        }

        // Navigate to success page or projects
        setTimeout(() => {
          navigate('/purchase-complete');
        }, 2000);
      } else if (response.data?.status === 'failed' || response.data?.status === 'cancelled') {
        // Payment failed
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
        if (timeUpdateIntervalRef.current) {
          clearInterval(timeUpdateIntervalRef.current);
          timeUpdateIntervalRef.current = null;
        }

        toast({
          title: t('checkout.paymentNotProcessed'),
          description: t('checkout.paymentNotProcessedDesc'),
          variant: "destructive",
        });

        setPixPaymentData(null);
      }
      // If status is still waiting_payment, continue polling
    } catch (error) {
      console.error('Error checking payment status:', error);
    }
  };

  const startPollingPaymentStatus = (paymentId: string) => {
    // Clear any existing interval
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }

    // Check immediately
    checkPixPaymentStatus(paymentId);

    // Then poll every 5 seconds
    pollingIntervalRef.current = setInterval(() => {
      checkPixPaymentStatus(paymentId);
    }, 5000);
  };

  const handleValidateCoupon = async () => {
    if (!couponCode.trim()) {
      toast({
        title: t('checkout.couponRequired'),
        description: t('checkout.enterCouponCodeRequired'),
        variant: "destructive",
      });
      return;
    }

    try {
      const { data } = await validateCoupon({
        variables: {
          code: couponCode.trim(),
          planId: plan.documentId,
          recurrency: isYearly ? 'year' : 'month'
        }
      });

      if (data?.validateCoupon?.valid && data.validateCoupon.coupon) {
        setValidatedCoupon(data.validateCoupon.coupon);
        toast({
          title: t('checkout.couponApplied', { code: data.validateCoupon.coupon.code }),
          description: t('checkout.discount', { percent: data.validateCoupon.coupon.discount }),
        });
      } else {
        setValidatedCoupon(null);
        const errorMessage = data?.validateCoupon?.error
          ? translateCouponError(data.validateCoupon.error)
          : t('checkout.couponNotValidForPlan');
        toast({
          title: t('checkout.couponInvalid'),
          description: errorMessage,
          variant: "destructive",
        });
      }
    } catch (error: any) {
      console.error('Coupon validation error:', error);
      setValidatedCoupon(null);
      toast({
        title: t('checkout.couponValidationError'),
        description: t('checkout.couponValidationErrorDesc'),
        variant: "destructive",
      });
    }
  };

  const handleRemoveCoupon = () => {
    setCouponCode("");
    setValidatedCoupon(null);
    couponFromUrlRef.current = null;
  };

  const handleStripeCheckout = async () => {
    setLoading(true);
    try {
      const response = await authAxios.post('/api/subscriptions/stripe/checkout-session', {
        planId: plan.documentId,
        yearly: isYearly,
        returnUrl: `${window.location.origin}/purchase-complete`,
        cancelUrl: window.location.href,
        coupon: validatedCoupon?.code,
        currency,
      });

      if (response.data?.url) {
        trackEvent("subscription_checkout_started", {
          plan_slug: plan.slug,
          value: priceValue,
          currency,
          payment_method: paymentMethod,
        });
        await addUserTag({ variables: { name: 'started-checkout' } });
        await addUserTag({ variables: { name: 'started-checkout-stripe' } });
        window.location.href = response.data.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error: any) {
      console.error('Checkout error:', error);
      toast({
        title: t('checkout.checkoutError'),
        description: isStoreManagedError(error)
          ? storeManagedErrorMessage
          : (error.response?.data?.message || t('checkout.checkoutErrorDesc')),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const formatDocument = (value: string, type: "cpf" | "cnpj") => {
    const cleaned = value.replace(/\D/g, '');

    if (type === "cpf") {
      return cpf.format(cleaned);
    } else {
      return cnpj.format(cleaned);
    }
  };

  const validateDocument = (value: string, type: "cpf" | "cnpj") => {
    const cleaned = value.replace(/\D/g, '');

    if (type === "cpf") {
      return cpf.isValid(cleaned);
    } else {
      return cnpj.isValid(cleaned);
    }
  };

  const handleDocumentChange = (value: string) => {
    const formatted = formatDocument(value, documentType);
    setPixFormData({ ...pixFormData, document: formatted });
  };

  const handlePixPayment = async () => {
    if (!pixFormData.document) {
      toast({
        title: t('checkout.requiredField'),
        description: t('checkout.fillDocumentField'),
        variant: "destructive",
      });
      return;
    }

    if (!validateDocument(pixFormData.document, documentType)) {
      toast({
        title: t('checkout.invalidDocument'),
        description: t('checkout.invalidDocumentDesc', { type: documentType.toUpperCase() }),
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      const response = await authAxios.post('/api/subscriptions/pix', {
        planId: plan.documentId,
        yearly: isYearly,
        documentType: documentType,
        documentNumber: pixFormData.document.replace(/\D/g, ''),
        coupon: validatedCoupon?.code,
      });

      if (response.data) {
        setPixPaymentData(response.data);

        // Start polling for payment status
        if (response.data.id) {
          startPollingPaymentStatus(response.data.id);
          trackEvent("subscription_checkout_started", {
            plan_slug: plan.slug,
            value: priceValue,
            currency,
            payment_method: "pix",
          });
          addUserTag({ variables: { name: 'started-checkout' } });
          addUserTag({ variables: { name: 'started-checkout-pix' } });
        }

        toast({
          title: t('checkout.qrCodeGenerated'),
          description: t('checkout.qrCodeGeneratedDesc'),
          variant: "default",
        });
      }
    } catch (error: any) {
      console.error('PIX error:', error);
      toast({
        title: t('checkout.pixError'),
        description: isStoreManagedError(error)
          ? storeManagedErrorMessage
          : isPhoneInvalidError(error)
          ? t('checkout.invalidPhoneDesc')
          : (error.response?.data?.message || t('checkout.pixErrorDesc')),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePayment = () => {
    if (storeManagedMessage) return;
    if (paymentMethod === "wallet") {
      const needsDocument = !walletApplication.coversFully && walletRemainderMethod === "pix";
      if (needsDocument) {
        if (!pixFormData.document) {
          toast({
            title: t('checkout.requiredField'),
            description: t('checkout.fillDocumentField'),
            variant: "destructive",
          });
          return;
        }
        if (!validateDocument(pixFormData.document, documentType)) {
          toast({
            title: t('checkout.invalidDocument'),
            description: t('checkout.invalidDocumentDesc', { type: documentType.toUpperCase() }),
            variant: "destructive",
          });
          return;
        }
      }
      setWalletConfirmOpen(true);
      return;
    }
    if (paymentMethod === "credit_card" || paymentMethod === "boleto") {
      handleStripeCheckout();
    } else if (paymentMethod === "pix") {
      handlePixPayment();
    }
  };

  const walletErrorToast = (message: string) => {
    const known: Record<string, string> = {
      wallet_balance_too_low: t('checkout.walletErrorBalanceTooLow'),
      payout_request_already_pending: t('checkout.walletErrorPayoutPending'),
      remainder_method_required: t('checkout.walletErrorRemainderRequired'),
      phone_invalid: t('checkout.invalidPhoneDesc'),
    };
    if (message.includes('store_managed')) {
      toast({
        title: t('checkout.checkoutError'),
        description: storeManagedErrorMessage,
        variant: "destructive",
      });
      return;
    }
    const match = Object.keys(known).find((code) => message.includes(code));
    toast({
      title: t('checkout.checkoutError'),
      description: match ? known[match] : t('checkout.checkoutErrorDesc'),
      variant: "destructive",
    });
  };

  const handleConfirmWalletRedemption = async () => {
    if (walletSubmitting) return;
    setWalletSubmitting(true);
    try {
      const isPartial = !walletApplication.coversFully;
      const { data } = await redeemWalletForPlan({
        variables: {
          planId: plan.documentId,
          yearly: isYearly,
          remainderMethod: isPartial ? walletRemainderMethod : null,
          document: isPartial && walletRemainderMethod === "pix"
            ? { number: pixFormData.document.replace(/\D/g, ''), type: documentType }
            : null,
          coupon: validatedCoupon?.code || null,
        },
      });

      trackEvent("subscription_checkout_started", {
        plan_slug: plan.slug,
        value: basePriceValue,
        currency,
        payment_method: "wallet",
        wallet_remainder: isPartial ? walletRemainderMethod : "none",
      });
      addUserTag({ variables: { name: 'started-checkout' } });
      addUserTag({ variables: { name: 'started-checkout-wallet' } });

      const outcome = resolveRedeemOutcome(data?.redeemWalletForPlan);
      if (outcome.action === "completed") {
        toast({
          title: t('checkout.walletRedeemSuccess'),
          description: t('checkout.walletRedeemSuccessDesc'),
        });
        try {
          await apolloClient.refetchQueries({ include: [GET_MY_DATA, MY_CPM_WALLET] });
        } catch (refetchError) {
          console.error('Error refetching user data:', refetchError);
        }
        setWalletConfirmOpen(false);
        navigate('/purchase-complete');
      } else if (outcome.action === "redirect") {
        window.location.href = outcome.url;
      } else if (outcome.action === "pix") {
        setWalletConfirmOpen(false);
        setPixPaymentData(outcome.pix as typeof pixPaymentData);
        if (outcome.pix.id) {
          startPollingPaymentStatus(String(outcome.pix.id));
        }
        toast({
          title: t('checkout.qrCodeGenerated'),
          description: t('checkout.qrCodeGeneratedDesc'),
        });
      } else {
        walletErrorToast('');
      }
    } catch (error: any) {
      console.error('Wallet redemption error:', error);
      walletErrorToast(error?.message || '');
    } finally {
      setWalletSubmitting(false);
    }
  };

  const { value: planValue, valueYearly: planValueYearly } = getPlanPrices(plan, isBRL);
  const basePriceValue = isYearly ? planValueYearly : planValue;
  const priceValue = computeDiscountedPrice(basePriceValue, validatedCoupon);
  const discountAmount = computeDiscountAmount(basePriceValue, validatedCoupon);
  const originalValue = isYearly ? planValue * 12 : undefined;
  const features = buildFeatures(plan);

  const walletAvailableCents = wallet?.available_cents ?? 0;
  // Wallet redemption is BRL-only. Invoice cents match the backend: coupon
  // (when validated) reduces the catalog first, then the plan-tier wallet
  // discount applies to the wallet-funded share.
  const canPayWithWallet = isBRL && walletAvailableCents > 0;
  const walletDiscountPercent = walletPlanDiscountPercent(plan);
  const walletApplication = resolveWalletChargePlan({
    availableCents: walletAvailableCents,
    invoicePriceCents: Math.round(priceValue * 100),
    discountPercent: walletDiscountPercent,
  });
  const remainderMethodLabel = {
    credit_card: t('checkout.creditCard'),
    pix: 'PIX',
  }[walletRemainderMethod];

  return (
    <Layout onStartTour={() => {}}>
      <div className="flex-1 p-4 lg:p-6">
        <div className="max-w-4xl mx-auto">
          <Button
            variant="ghost"
            onClick={() => navigate('/plans')}
            className="mb-6"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t('checkout.backToPlans')}
          </Button>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Resumo do Plano */}
            <Card>
              <CardHeader>
                <CardTitle>{t('checkout.planSummary')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <h3 className="text-2xl font-bold">{plan.name}</h3>
                  <div className="mt-2">
                    <p className="text-3xl font-bold text-primary">
                      {formatCurrency(isYearly ? priceValue / 12 : priceValue)}
                      <span className="text-lg text-muted-foreground font-normal"> {t('common.perMonth')}</span>
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {isYearly ? t('common.billedAnnually') : ''}
                    </p>
                    {isYearly && (
                      <p className="text-base text-muted-foreground mt-1">
                        {t('checkout.annualTotal')} {formatCurrency(priceValue)}
                      </p>
                    )}
                    {originalValue && (
                      <p className="text-sm text-muted-foreground line-through">
                        {formatCurrency(originalValue)}
                      </p>
                    )}
                    {isYearly && originalValue && (
                      <Badge variant="outline" className="mt-2 bg-primary/10 text-primary border-primary/30">
                        {t('checkout.save', { percent: Math.round((1 - (basePriceValue / originalValue)) * 100) })}
                      </Badge>
                    )}
                  </div>
                </div>

                {/* Coupon Section */}
                <div className="space-y-3 pt-4 border-t">
                  <h4 className="font-semibold">{t('checkout.discountCode')}</h4>
                  {!validatedCoupon ? (
                    <div className="flex gap-2">
                      <Input
                        placeholder={t('checkout.enterCouponCode')}
                        value={couponCode}
                        onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            handleValidateCoupon();
                          }
                        }}
                        disabled={couponValidating}
                      />
                      <Button
                        onClick={handleValidateCoupon}
                        disabled={couponValidating || !couponCode.trim()}
                        variant="outline"
                      >
                        {couponValidating ? t('checkout.validating') : t('checkout.apply', 'Apply')}
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between p-3 bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800 rounded-lg">
                        <div className="flex items-center gap-2">
                          <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
                          <div>
                            <p className="font-semibold text-green-700 dark:text-green-300">
                              {t('checkout.couponApplied', { code: validatedCoupon.code })}
                            </p>
                            <p className="text-sm text-green-600 dark:text-green-400">
                              {t('checkout.discount', { percent: validatedCoupon.discount })}
                              {validatedCoupon.cycles === -1 ? ` ${t('checkout.discountForever')}` : validatedCoupon.cycles && (() => {
                                const key = isYearly ? 'checkout.discountForYears' : 'checkout.discountForMonths';
                                return ` ${t(key, { cycles: validatedCoupon.cycles, count: validatedCoupon.cycles })}`;
                              })()}
                            </p>
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={handleRemoveCoupon}
                          className="text-green-700 dark:text-green-300 hover:text-green-800 dark:hover:text-green-200"
                        >
                          {t('checkout.remove', 'Remove')}
                        </Button>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">{t('checkout.subtotal')}</span>
                        <span className="line-through text-muted-foreground">
                          {formatCurrency(isYearly ? basePriceValue / 12 : basePriceValue)}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm font-semibold text-green-600 dark:text-green-400">
                        <span>{t('checkout.discountAmount')}</span>
                        <span>-{formatCurrency(isYearly ? discountAmount / 12 : discountAmount)}</span>
                      </div>
                    </div>
                  )}
                </div>

                <div className="space-y-3">
                  <h4 className="font-semibold">{t('checkout.featuresIncluded')}</h4>
                  {features.map((feature, idx) => (
                    <div key={idx} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                      <span className={`text-sm ${feature.comingSoon ? 'text-muted-foreground' : ''}`}>
                        {feature.label}{feature.comingSoon ? ` (${t('common.comingSoon')})` : ''}
                      </span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Método de Pagamento */}
            <Card>
              <CardHeader>
                <CardTitle>{t('checkout.paymentMethod')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <RadioGroup value={paymentMethod} onValueChange={(value: any) => setPaymentMethod(value)}>
                  <div className="space-y-3">
                    {canPayWithWallet && (
                      <label className="flex items-center space-x-3 cursor-pointer p-3 rounded-lg border border-lime-500/40 bg-lime-500/5 hover:bg-lime-500/10 transition-colors">
                        <RadioGroupItem value="wallet" />
                        <Wallet className="w-5 h-5 text-lime-500" />
                        <span className="flex-1">
                          Saldo da carteira
                          <span className="block text-xs text-muted-foreground">
                            Disponível: {formatCents(walletAvailableCents, wallet?.currency ?? 'BRL')} · sem taxas
                          </span>
                        </span>
                      </label>
                    )}

                    {!canPayWithWallet && isRedemptionHoldPending && (
                      <div className="p-3 rounded-lg border border-amber-500/40 bg-amber-500/5 space-y-2">
                        <p className="text-xs text-amber-600 dark:text-amber-400">
                          {t('wallet.pendingHoldNotice')}
                        </p>
                        <CancelPendingHoldButton
                          className="w-full"
                          onCancelled={async () => {
                            await refetchWallet();
                          }}
                        />
                      </div>
                    )}

                    <label className="flex items-center space-x-3 cursor-pointer p-3 rounded-lg border hover:bg-muted/50 transition-colors">
                      <RadioGroupItem value="credit_card" />
                      <CreditCard className="w-5 h-5" />
                      <span className="flex-1">{t('checkout.creditCard')}</span>
                    </label>

                    {isBRL && (
                      <label className="flex items-center space-x-3 cursor-pointer p-3 rounded-lg border hover:bg-muted/50 transition-colors">
                        <RadioGroupItem value="boleto" />
                        <FileText className="w-5 h-5" />
                        <span className="flex-1">{t('checkout.bankSlip')}</span>
                      </label>
                    )}

                    {isBRL && (
                      <label className="flex items-center space-x-3 cursor-pointer p-3 rounded-lg border hover:bg-muted/50 transition-colors">
                        <RadioGroupItem value="pix" />
                        <QrCode className="w-5 h-5" />
                        <span className="flex-1">PIX</span>
                      </label>
                    )}
                  </div>
                </RadioGroup>

                {paymentMethod === "wallet" && !pixPaymentData && (
                  <div className="space-y-4">
                    <div className="p-4 rounded-lg bg-lime-500/10 border border-lime-500/30 space-y-2">
                      {walletApplication.discountCents > 0 && (
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">
                            {t('checkout.walletDiscountAmount', { percent: walletDiscountPercent })}
                          </span>
                          <span className="font-semibold text-lime-600 dark:text-lime-400">
                            − {formatCents(walletApplication.discountCents, wallet?.currency ?? 'BRL')}
                          </span>
                        </div>
                      )}
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Saldo aplicado</span>
                        <span className="font-semibold text-lime-600 dark:text-lime-400">
                          − {formatCents(walletApplication.appliedCents, wallet?.currency ?? 'BRL')}
                        </span>
                      </div>
                      {walletApplication.coversFully ? (
                        <p className="text-sm font-semibold text-center pt-1">
                          Saldo cobre 100% do valor — nada a pagar.
                        </p>
                      ) : (
                        <div className="flex justify-between text-sm pt-1 border-t border-lime-500/20">
                          <span className="text-muted-foreground">Restante a pagar</span>
                          <span className="font-semibold">
                            {formatCents(walletApplication.remainingCents, wallet?.currency ?? 'BRL')}
                          </span>
                        </div>
                      )}
                    </div>

                    {!walletApplication.coversFully && (
                      <div className="space-y-2">
                        <Label>Como deseja pagar o restante?</Label>
                        <RadioGroup
                          value={walletRemainderMethod}
                          onValueChange={(value: any) => setWalletRemainderMethod(value)}
                        >
                          <div className="space-y-2">
                            <label className="flex items-center space-x-3 cursor-pointer p-2.5 rounded-lg border hover:bg-muted/50 transition-colors">
                              <RadioGroupItem value="credit_card" />
                              <CreditCard className="w-4 h-4" />
                              <span className="flex-1 text-sm">{t('checkout.creditCard')}</span>
                            </label>
                            {isBRL && (
                              <label className="flex items-center space-x-3 cursor-pointer p-2.5 rounded-lg border hover:bg-muted/50 transition-colors">
                                <RadioGroupItem value="pix" />
                                <QrCode className="w-4 h-4" />
                                <span className="flex-1 text-sm">PIX</span>
                              </label>
                            )}
                          </div>
                        </RadioGroup>

                        {walletRemainderMethod === "pix" && (
                          <div className="space-y-4 pt-2">
                            <div>
                              <Label htmlFor="walletDocumentType">{t('checkout.documentType')}</Label>
                              <Select
                                value={documentType}
                                onValueChange={(value: "cpf" | "cnpj") => {
                                  setDocumentType(value);
                                  setPixFormData({ ...pixFormData, document: "" });
                                }}
                              >
                                <SelectTrigger id="walletDocumentType">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="cpf">CPF</SelectItem>
                                  <SelectItem value="cnpj">CNPJ</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <Label htmlFor="walletDocument">
                                {documentType === "cpf" ? "CPF" : "CNPJ"}
                              </Label>
                              <Input
                                id="walletDocument"
                                value={pixFormData.document}
                                onChange={(e) => handleDocumentChange(e.target.value)}
                                placeholder={documentType === "cpf" ? "000.000.000-00" : "00.000.000/0000-00"}
                                maxLength={documentType === "cpf" ? 14 : 18}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    <p className="text-xs text-muted-foreground text-center">
                      {t('checkout.walletCouponNote')}
                      {walletDiscountPercent > 0 && (
                        <> {t('checkout.walletDiscountNote', { percent: walletDiscountPercent })}</>
                      )}
                    </p>
                  </div>
                )}

                {paymentMethod === "pix" && !pixPaymentData && (
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="documentType">{t('checkout.documentType')}</Label>
                      <Select
                        value={documentType}
                        onValueChange={(value: "cpf" | "cnpj") => {
                          setDocumentType(value);
                          setPixFormData({ ...pixFormData, document: "" });
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="cpf">CPF</SelectItem>
                          <SelectItem value="cnpj">CNPJ</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="document">
                        {documentType === "cpf" ? "CPF" : "CNPJ"}
                      </Label>
                      <Input
                        id="document"
                        value={pixFormData.document}
                        onChange={(e) => handleDocumentChange(e.target.value)}
                        placeholder={documentType === "cpf" ? "000.000.000-00" : "00.000.000/0000-00"}
                        maxLength={documentType === "cpf" ? 14 : 18}
                      />
                    </div>
                  </div>
                )}

                {pixPaymentData && (
                  <div className="space-y-6">
                    <div className="text-center space-y-4">
                      <div className="flex items-center justify-center gap-2 text-amber-600">
                        <Clock className="w-5 h-5 animate-pulse" />
                        <p className="text-lg font-semibold">{t('checkout.awaitingPayment')}</p>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {t('checkout.scanQrCode')}
                      </p>

                      {pixPaymentData.expires_at && (
                        <Badge
                          variant="outline"
                          className={(() => {
                            const { totalMinutes } = formatPixCountdown(pixPaymentData.expires_at, currentTime);
                            if (totalMinutes <= 2) {
                              return "bg-red-50 text-red-700 border-red-300 animate-pulse";
                            } else if (totalMinutes <= 5) {
                              return "bg-orange-50 text-orange-700 border-orange-300";
                            } else {
                              return "bg-amber-50 text-amber-700 border-amber-300";
                            }
                          })()}
                        >
                          {formatExpirationTime(pixPaymentData.expires_at)}
                        </Badge>
                      )}

                      <div className="bg-white p-4 rounded-lg inline-block shadow-lg">
                        <img
                          src={pixPaymentData.qr_code_url}
                          alt="QR Code PIX"
                          className="w-64 h-64"
                        />
                      </div>

                      <div className="space-y-2">
                        <p className="text-sm font-medium">{t('checkout.orCopyPixCode')}</p>
                        <div className="flex items-center gap-2 max-w-md mx-auto">
                          <Input
                            value={pixPaymentData.qr_code}
                            readOnly
                            className="text-xs font-mono"
                          />
                          <Button
                            variant="outline"
                            size="icon"
                            onClick={() => copyPixCode(pixPaymentData.qr_code)}
                            title={t('checkout.pixCodeCopied')}
                          >
                            <Copy className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <p className="text-xs text-muted-foreground">
                          {t('checkout.paymentAutoConfirm')}
                        </p>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            if (pollingIntervalRef.current) {
                              clearInterval(pollingIntervalRef.current);
                              pollingIntervalRef.current = null;
                            }
                            if (timeUpdateIntervalRef.current) {
                              clearInterval(timeUpdateIntervalRef.current);
                              timeUpdateIntervalRef.current = null;
                            }
                            setPixPaymentData(null);
                          }}
                        >
                          {t('checkout.generateNewQrCode')}
                        </Button>
                      </div>
                    </div>
                  </div>
                )}

                {!pixPaymentData && (
                  <Button
                    onClick={handlePayment}
                    variant="gradient"
                    disabled={loading || walletSubmitting}
                    size="lg"
                    className={cn(
                      "w-full",
                      paymentMethod === "wallet" && "bg-lime-400 hover:bg-lime-500 text-lime-950"
                    )}
                  >
                    {paymentMethod === "wallet" ? (
                      <span className="flex items-center gap-2">
                        <Sparkles className="w-4 h-4" />
                        {t('checkout.walletPayButton')}
                      </span>
                    ) : (
                      loading ? t('checkout.processing') : t('checkout.finalizePayment')
                    )}
                  </Button>
                )}
                {isTester && (
                  <RouterLink to="/purchase-complete">
                    <Button
                      variant="outline"
                      disabled={loading}
                      size="lg"
                      className="w-full mt-4"
                    >
                      {t('checkout.simulatePurchase')}
                    </Button>
                  </RouterLink>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      <Dialog
        open={walletConfirmOpen}
        onOpenChange={(open) => {
          if (!open && walletSubmitting) return;
          setWalletConfirmOpen(open);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-lime-500" />
              {t('checkout.walletConfirmTitle')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">{t('checkout.walletPlanLabel')}</span>
              <span className="font-medium">{plan.name} · {isYearly ? t('pricing.yearly') : t('pricing.monthly')}</span>
            </div>
            {walletApplication.discountCents > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">
                  {t('checkout.walletDiscountAmount', { percent: walletDiscountPercent })}
                </span>
                <span className="font-semibold text-lime-600 dark:text-lime-400">
                  − {formatCents(walletApplication.discountCents, wallet?.currency ?? 'BRL')}
                </span>
              </div>
            )}
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">{t('checkout.walletBalanceApplied')}</span>
              <span className="font-semibold text-lime-600 dark:text-lime-400">
                − {formatCents(walletApplication.appliedCents, wallet?.currency ?? 'BRL')}
              </span>
            </div>
            {walletApplication.coversFully ? (
              <div className="p-3 rounded-lg bg-lime-500/10 border border-lime-500/30 text-sm text-center font-medium">
                {t('checkout.walletFullCoverage')}
              </div>
            ) : (
              <>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">
                    {t('checkout.walletRemainderVia', { method: remainderMethodLabel })}
                  </span>
                  <span className="font-semibold">
                    {formatCents(walletApplication.remainingCents, wallet?.currency ?? 'BRL')}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground">
                  {t('checkout.walletHoldNote')}
                </p>
              </>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setWalletConfirmOpen(false)}
              disabled={walletSubmitting}
            >
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleConfirmWalletRedemption}
              disabled={walletSubmitting}
              className="bg-lime-400 hover:bg-lime-500 text-lime-950 font-semibold"
            >
              {walletSubmitting ? t('checkout.processing') : t('checkout.walletConfirmButton')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default PlanCheckout;
